/**
 * /admin/platform/llm/rates — per-model price sheet editor.
 *
 * Reads /v1/admin/platform/llm/rates and renders one row per
 * configured model. Operators add a row for any model_id that
 * appears in the call log so the cost column stops reading as
 * zero. Prices are stored as integer micros of AUD per million
 * tokens (1 AUD = 1,000,000 micros); the form accepts plain AUD
 * with up to four decimal places and converts on submit.
 *
 * The dashboard's models_missing_rates list (when reachable via
 * URL state) is rendered as a chip strip at the top so an operator
 * arriving from the dashboard banner sees exactly which models
 * need a row.
 */

import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { adminApi, type AdminApiError, type LlmModelRate } from "./api";

interface DraftRow {
  model_id: string;
  input_aud_per_mtok: string;
  output_aud_per_mtok: string;
}

const EMPTY_DRAFT: DraftRow = {
  model_id: "",
  input_aud_per_mtok: "",
  output_aud_per_mtok: "",
};

function microsToAud(micros: number): string {
  return (micros / 1_000_000).toFixed(4);
}

function audToMicros(aud: string): number | null {
  const trimmed = aud.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  // Round half-up to the nearest micro so a hand-typed "0.18"
  // round-trips through audToMicros / microsToAud cleanly.
  return Math.round(value * 1_000_000);
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function LlmRatesPage() {
  const [params] = useSearchParams();
  const missingFromQuery = useMemo(() => {
    const raw = params.get("missing");
    if (raw === null || raw.length === 0) {
      return [];
    }
    return raw.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
  }, [params]);

  const [rates, setRates] = useState<LlmModelRate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, DraftRow>>({});
  const [newDraft, setNewDraft] = useState<DraftRow>(EMPTY_DRAFT);
  const [saving, setSaving] = useState<string | null>(null);

  async function reload() {
    try {
      const result = await adminApi.listLlmRates();
      setRates(result.items);
      // Re-seed editable drafts from server state. Anything the
      // operator was mid-edit is intentionally discarded; the UI
      // only persists drafts after a save anyway.
      const next: Record<string, DraftRow> = {};
      for (const r of result.items) {
        next[r.model_id] = {
          model_id: r.model_id,
          input_aud_per_mtok: microsToAud(r.input_micros_per_mtok),
          output_aud_per_mtok: microsToAud(r.output_micros_per_mtok),
        };
      }
      setDrafts(next);
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Could not load rates.");
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const ratedIds = useMemo(() => new Set(rates.map((r) => r.model_id)), [rates]);
  const stillMissing = useMemo(
    () => missingFromQuery.filter((m) => !ratedIds.has(m)),
    [missingFromQuery, ratedIds],
  );

  async function saveRow(modelId: string, draft: DraftRow) {
    setError(null);
    setInfo(null);
    const inputMicros = audToMicros(draft.input_aud_per_mtok);
    const outputMicros = audToMicros(draft.output_aud_per_mtok);
    if (inputMicros === null || outputMicros === null) {
      setError(
        "Both input and output prices must be non-negative numbers in AUD per million tokens.",
      );
      return;
    }
    setSaving(modelId);
    try {
      await adminApi.putLlmRate({
        model_id: modelId,
        input_micros_per_mtok: inputMicros,
        output_micros_per_mtok: outputMicros,
        // updated_at is server-set; we send a placeholder the
        // server overwrites on persist.
        updated_at: new Date().toISOString(),
      });
      setInfo(`Saved rate for ${modelId}.`);
      await reload();
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Save failed.");
    } finally {
      setSaving(null);
    }
  }

  async function addNewRow(e: FormEvent) {
    e.preventDefault();
    const modelId = newDraft.model_id.trim();
    if (modelId.length === 0) {
      setError("Model id is required.");
      return;
    }
    await saveRow(modelId, newDraft);
    if (error === null) {
      setNewDraft(EMPTY_DRAFT);
    }
  }

  function prefillFromMissing(modelId: string) {
    setNewDraft({ model_id: modelId, input_aud_per_mtok: "", output_aud_per_mtok: "" });
  }

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h2>LLM rate sheet</h2>
        <p className="admin-page-blurb">
          Per-model prices in AUD per million tokens. Costs are computed at
          the time of each call and stored on the ``model_call`` row, so
          editing a rate here changes future calls only and never rewrites
          history. A model with no rate row records as zero spend on the
          dashboard.
        </p>
      </header>

      {error !== null && <div className="banner banner-error">{error}</div>}
      {info !== null && <div className="banner banner-info">{info}</div>}

      {stillMissing.length > 0 && (
        <div className="banner banner-warn">
          <strong>Models with calls but no rate:</strong>{" "}
          {stillMissing.map((m, idx) => (
            <span key={m}>
              <button
                type="button"
                className="btn-link"
                onClick={() => prefillFromMissing(m)}
              >
                {m}
              </button>
              {idx < stillMissing.length - 1 ? ", " : ""}
            </span>
          ))}
          . Click a name to prefill the form below.
        </div>
      )}

      {!loaded && <div>Loading…</div>}

      {loaded && (
        <>
          <h3 className="admin-section-title">Configured rates</h3>
          {rates.length === 0 ? (
            <div className="empty-row">
              No rates configured. Add one below; until at least one rate
              row exists, the dashboard's spend column will be zero.
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Model id</th>
                  <th>Input (AUD / Mtok)</th>
                  <th>Output (AUD / Mtok)</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => {
                  const draft = drafts[r.model_id] ?? {
                    model_id: r.model_id,
                    input_aud_per_mtok: microsToAud(r.input_micros_per_mtok),
                    output_aud_per_mtok: microsToAud(r.output_micros_per_mtok),
                  };
                  return (
                    <tr key={r.model_id}>
                      <td>{r.model_id}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          value={draft.input_aud_per_mtok}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [r.model_id]: {
                                ...draft,
                                input_aud_per_mtok: e.target.value,
                              },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          value={draft.output_aud_per_mtok}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [r.model_id]: {
                                ...draft,
                                output_aud_per_mtok: e.target.value,
                              },
                            }))
                          }
                        />
                      </td>
                      <td>{formatTime(r.updated_at)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={saving === r.model_id}
                          onClick={() => saveRow(r.model_id, draft)}
                        >
                          {saving === r.model_id ? "Saving…" : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <h3 className="admin-section-title">Add a rate</h3>
          <form onSubmit={addNewRow} className="admin-card-body">
            <div className="field-row">
              <div className="field">
                <label htmlFor="rate-new-model">Model id</label>
                <input
                  id="rate-new-model"
                  type="text"
                  value={newDraft.model_id}
                  onChange={(e) =>
                    setNewDraft({ ...newDraft, model_id: e.target.value })
                  }
                  placeholder="claude-sonnet-4-6"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="rate-new-input">Input (AUD / Mtok)</label>
                <input
                  id="rate-new-input"
                  type="number"
                  min="0"
                  step="0.0001"
                  value={newDraft.input_aud_per_mtok}
                  onChange={(e) =>
                    setNewDraft({
                      ...newDraft,
                      input_aud_per_mtok: e.target.value,
                    })
                  }
                  placeholder="4.50"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="rate-new-output">Output (AUD / Mtok)</label>
                <input
                  id="rate-new-output"
                  type="number"
                  min="0"
                  step="0.0001"
                  value={newDraft.output_aud_per_mtok}
                  onChange={(e) =>
                    setNewDraft({
                      ...newDraft,
                      output_aud_per_mtok: e.target.value,
                    })
                  }
                  placeholder="22.50"
                  required
                />
              </div>
            </div>
            <div className="actions">
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={saving !== null}
              >
                {saving === newDraft.model_id ? "Saving…" : "Add rate"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
